import { FC, useState } from 'react';
import {
  useProviderUserMembersV2Query,
  Feature,
} from '../../../../generated/graphql';
import { Transition } from '@headlessui/react';
import { defaultTransitionProps } from '../../../lib/animation';
import { PlusSmIcon } from '@heroicons/react/outline';
import Button from '../../../components/Button';
import AddTeamMembersModal from './Modals/AddTeamMembersModal';
import TeamMembersTable from './TeamMembersTable';
import ToastAlert from '../../../components/ToastAlert';
import toast from 'react-hot-toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { AnalyticsPage } from '../../../../lib/analytics';
import { useGlobalModalsContext } from '../../../../contexts/GlobalModalsContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';

const TeamSection: FC = () => {
  const { showUpgradeModal } = useGlobalModalsContext();
  const { authedProviderUser, refreshAuthedProviderUser } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const isSeatTypeEnabled = isFeatureEnabled(
    Feature.EnableSwitchingProviderContext,
  );

  const [isAddTeamMembersModalOpen, setIsAddTeamMembersModalOpen] =
    useState(false);

  const {
    data: teamMembersData,
    loading: isLoadingTeamMembers,
    error: teamMembersError,
    refetch: refetchTeamMembers,
  } = useProviderUserMembersV2Query({
    onError(error) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong getting your team members. Please try refreshing."
          level="error"
        />
      ));
    },
  });

  const teamMemberProviderUsers = teamMembersData?.providerUserMembers ?? [];

  const seatUsage = authedProviderUser?.provider?.seatUsage;
  const seatLimitReached = isSeatTypeEnabled
    ? seatUsage?.remainingStaffSeats <= 0 &&
      seatUsage?.remainingCollaboratorSeats <= 0
    : seatUsage?.remainingStaffSeats <= 0;

  return (
    <>
      <Transition
        show={!isLoadingTeamMembers && !teamMembersError}
        {...defaultTransitionProps}
      >
        <div className="h-full">
          <div className="my-6 flex justify-end">
            <div className="flex items-center gap-x-6">
              {seatUsage && (
                <div className="flex items-center gap-x-3">
                  <div className="flex items-center gap-x-2">
                    <span className="text-small-caption text-neutral-110">
                      Staff
                    </span>
                    <span className="text-caption font-medium text-neutral-125">
                      {seatUsage.staffSeats
                        ? `${seatUsage.usedStaffSeats}/${seatUsage.staffSeats}`
                        : `${seatUsage.usedStaffSeats}`}
                    </span>
                  </div>
                  {isSeatTypeEnabled && (
                    <>
                      <div className="h-4 w-px bg-neutral-25" />
                      <div className="flex items-center gap-x-2">
                        <span className="text-small-caption text-neutral-110">
                          Collaborators
                        </span>
                        <span className="text-caption font-medium text-neutral-125">
                          {seatUsage.collaboratorSeats
                            ? `${seatUsage.usedCollaboratorSeats}/${seatUsage.collaboratorSeats}`
                            : `${seatUsage.usedCollaboratorSeats}`}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <Button
                title="Add team members"
                IconComponent={PlusSmIcon}
                size="small"
                iconPosition="right"
                theme="primary"
                onClick={() => {
                  if (seatLimitReached) {
                    showUpgradeModal(AnalyticsPage.SettingsTeam);
                    return;
                  }
                  setIsAddTeamMembersModalOpen(true);
                }}
              />
            </div>
          </div>

          <TeamMembersTable
            providerUserMembers={teamMemberProviderUsers}
            refetchTeamMembers={refetchTeamMembers}
            refreshAuthedProviderUser={refreshAuthedProviderUser}
            seatLimitReached={seatLimitReached}
            onSeatLimitReached={() => {
              showUpgradeModal(AnalyticsPage.SettingsTeam);
            }}
          />
        </div>
      </Transition>
      <AddTeamMembersModal
        isOpen={isAddTeamMembersModalOpen}
        existingTeamMembers={teamMemberProviderUsers}
        setClosed={() => setIsAddTeamMembersModalOpen(false)}
        refetchTeamMembers={refetchTeamMembers}
        seatUsage={seatUsage}
      />
    </>
  );
};

export default TeamSection;
